import React from 'react';
import useResponsiveScreen from 'src/hooks/useResponsiveScreen';
import { observer } from 'mobx-react-lite';
import { ScrollView, useWindowDimensions, View } from 'react-native';
import { stylesDesktop, stylesMobile } from './UploadPicker.style';
import { ICON_NAMES } from '@photosi/rn-components/icon/icon.helper';
import { brand } from 'src/theme/colors';
import { MainLogoPressable } from '@photosi/rn-components/main-logo-pressable/MainLogoPressable';
import { ChatButton } from 'src/_shared/chat/ChatButton';
import { HelpButton } from '@photosi/rn-components/help-button/HelpButton';
import { StepCard } from '../step-card/StepCard';
import { DiscountInfo } from '../discount-info/DiscountInfo';
import { BoxStyleInfo, FormatInfo } from '@photosi/expo-photosi-sdk/build/PrintUtils';
import { STEPS, StepUpload } from './UploadSteps';
import Animated from 'react-native-reanimated';
import { PickerLoadingContent } from '@photosi/rn-components/modals/dynamic-modals/modal-upload-multisource/picker-loading-content/PickerLoadingContent';
import { useUploadPickerLogic } from './UploadPicker.logic';
import { Icon } from '@photosi/rn-components/icon/Icon';
import { useNetworkState } from 'src/editor-v3/hooks/useNetworkState';
import { PressableHovered } from '../pressable-hovered/PressableHovered';
import { FocusGroups } from '@photosi/rn-components/focus-provider/FocusProvider.constants';
import { TestIDs } from 'src/constants/testIDs.constants';

const STEP_TITLE_KEYS: { [key: string]: string } = {
  [STEPS.Format]: 'modal-configuration-steps:format-title',
  [STEPS.PaperType]: 'modal-configuration-steps:papertype-title',
  [STEPS.PaperFinish]: 'modal-configuration-steps:paperfinish-title',
  [STEPS.BoxStyle]: 'modal-configuration-steps:box-style-title',
};

export const UploadPicker = observer(() => {
  const { isDesktop } = useResponsiveScreen();

  const {
    i18n,
    discount,
    selectedCard,
    setSelectedCard,
    step,
    handleBack,
    onConfirmStep,
    isUploadingPhoto,
    setUploadingPhoto,
    selectedFormat,
    getCards,
    leftAnimatedStyle,
    rightAnimatedStyle,
    isRightVisible,
    isHappyCaseGroup,
    isPhotokitGroup,
    isAnyProductPresent,
    groupCode,
  } = useUploadPickerLogic();
  const isOnline = useNetworkState();

  const { width } = useWindowDimensions();
  const widthSize = width * 0.2;

  const styles = isDesktop ? stylesDesktop : stylesMobile;
  const isFirstOptionStep = step === STEPS.Format || step === STEPS.BoxStyle;
  const shouldShowHeaderButton = step !== STEPS.Loading && (!isFirstOptionStep || isAnyProductPresent);

  return (
    <View style={styles.wrapper}>
      <View style={styles.header}>
        <View style={styles.headerLeft}>
          {shouldShowHeaderButton && (
            <View style={styles.headetButton}>
              <PressableHovered
                style={styles.backButton}
                onPress={async () => handleBack()}
                accessibilityLabel={i18n.t('upload:back')}
                accessibilityRole="button"
                focusGroup={FocusGroups.HEADER}
                focusOrder={0}
              >
                <Icon
                  name={ICON_NAMES.ArrowLeft2}
                  width={isDesktop ? 20 : 16}
                  height={isDesktop ? 20 : 16}
                  color={brand.main.water}
                />
              </PressableHovered>
            </View>
          )}
        </View>
        <View style={styles.headerCenter}>
          <MainLogoPressable
            testID={TestIDs.PHOTOSI_LOGO}
            focusId={TestIDs.PHOTOSI_LOGO}
            focusOrder={1}
            focusGroup={FocusGroups.HEADER}
          />
        </View>
        <View style={styles.headerRight}>
          {!isDesktop && <HelpButton focusGroup={FocusGroups.HEADER} focusId={TestIDs.HELP_BUTTON} focusOrder={2} />}
          <ChatButton newGraphic focusGroup={FocusGroups.HEADER} focusOrder={3} />
        </View>
      </View>
      {isDesktop ? (
        <>
          <View style={[styles.body, { gap: isRightVisible ? 24 : 0 }]}>
            {/* accessibilityLiveRegion: screen readers announce the new step when it changes */}
            <Animated.View style={[styles.container, leftAnimatedStyle]} accessibilityLiveRegion="polite">
              {step === STEPS.Loading || !!getCards.length ? (
                <StepUpload
                  cards={getCards}
                  onConfirmStep={onConfirmStep}
                  selectedCard={selectedCard}
                  setSelectedCard={setSelectedCard}
                  step={step}
                  isUploadingPhoto={isUploadingPhoto}
                  setUploadingPhoto={setUploadingPhoto}
                  title={i18n.t(STEP_TITLE_KEYS[step])}
                  groupCode={groupCode}
                  isOnline={isOnline}
                />
              ) : (
                <PickerLoadingContent
                  text="upload:loading-wait"
                  customStyles={{
                    loadingContentDetails: styles.loadingContentDetails,
                    loadingContentText: styles.loadingContentText,
                  }}
                />
              )}
            </Animated.View>
            {!isPhotokitGroup && (
              <Animated.View style={[styles.containerRight, rightAnimatedStyle]}>
                <ScrollView style={styles.sideWrapper} contentContainerStyle={styles.sideWrapperContent}>
                  {discount?.length > 1 && <DiscountInfo discount={discount} />}
                  {selectedFormat && 'code' in selectedFormat && (
                    <StepCard
                      code={(selectedFormat as FormatInfo).code}
                      previewImageUrl={
                        !isHappyCaseGroup
                          ? ((selectedFormat as FormatInfo)?.previewImageUrl ?? '')
                          : (selectedFormat as BoxStyleInfo)?.previewUrl || ''
                      }
                      title={i18n.t(
                        !isHappyCaseGroup
                          ? `modal-configuration-steps:format:${(selectedFormat as FormatInfo)?.code?.split('.')?.[0]}`
                          : `modal-configuration-steps:box-style:${(selectedFormat as FormatInfo)?.code?.toLocaleLowerCase()}`,
                      )}
                      index={0}
                      avoidRadioButton
                      width={widthSize}
                    />
                  )}
                </ScrollView>
              </Animated.View>
            )}
          </View>
          <HelpButton focusGroup={FocusGroups.BOTTOM_BAR} focusId={TestIDs.HELP_BUTTON} focusOrder={0} />
        </>
      ) : (
        <View style={styles.body}>
          {step === STEPS.Loading || !!getCards.length ? (
            <View
              style={
                step !== STEPS.Format && step !== STEPS.PaperType && step !== STEPS.PaperFinish
                  ? styles.bodyInner
                  : styles.body
              }
              accessibilityLiveRegion="polite"
            >
              <StepUpload
                cards={getCards}
                onConfirmStep={onConfirmStep}
                selectedCard={selectedCard}
                setSelectedCard={setSelectedCard}
                step={step}
                isUploadingPhoto={isUploadingPhoto}
                setUploadingPhoto={setUploadingPhoto}
                discount={discount}
                selectedFormat={selectedFormat}
                title={i18n.t(STEP_TITLE_KEYS[step])}
                isHappyCaseGroup={isHappyCaseGroup}
                isPhotokitGroup={isPhotokitGroup}
                groupCode={groupCode}
                isOnline={isOnline}
              />
            </View>
          ) : (
            <PickerLoadingContent
              text="upload:loading-wait"
              customStyles={{
                loadingContentDetails: styles.loadingContentDetails,
                loadingContentText: styles.loadingContentText,
              }}
            />
          )}
        </View>
      )}
    </View>
  );
});
